import { useContext } from 'react'
import UserContext, { Roles } from '../contexts/UserContext'
import SectionContext from '../contexts/SectionContext'
import { canEditStock } from '../config/modulesByTenant'
import { hasPermission as checkPermission, resolveEffectivePermissions } from '../utils/permissions'

export const usePermissions = () => {
  const { user } = useContext(UserContext)
  const { permissions: sectionPermissions } = useContext(SectionContext)

  const isGlobalAdmin =
    (user?.role as any) === Roles.globalAdmin ||
    (user?.role as any) === 'ADMIN_GLOBAL' ||
    (typeof user?.role === 'string' && user.role.toUpperCase() === 'ADMIN_GLOBAL')

  const isTenantAdmin =
    (user?.role as any) === Roles.tenantAdmin ||
    (user?.role as any) === 'TENANT_ADMIN' ||
    (typeof user?.role === 'string' && user.role.toUpperCase() === 'TENANT_ADMIN')

  const isAdmin = isGlobalAdmin || isTenantAdmin

  const effectivePermissions = resolveEffectivePermissions(user?.permissions, sectionPermissions)

  const hasPermission = (permission: string, dependencies: string[] = []): boolean => {
    if (isAdmin) return true
    return checkPermission(effectivePermissions, permission)
  }

  const canEdit = {
    stock: (): boolean => isAdmin || canEditStock(effectivePermissions),
    certificates: (): boolean => hasPermission('certificates'),
    production: (): boolean => hasPermission('production'),
    orders: (): boolean => hasPermission('orders'),
    router: (): boolean => hasPermission('router'),
    accesses: (): boolean => hasPermission('accesses'),
    products: (): boolean => hasPermission('products'),
    resources: (): boolean => hasPermission('resources'),
    services: (): boolean => hasPermission('services'),
    equipment: (): boolean => hasPermission('equipment')
  }

  const canView = {
    stock: (): boolean => isAdmin || hasPermission('stock') || hasPermission('materia_prima'),
    certificates: (): boolean => hasPermission('certificates'),
    production: (): boolean => hasPermission('production'),
    orders: (): boolean => hasPermission('orders'),
    router: (): boolean => hasPermission('router'),
    accesses: (): boolean => hasPermission('accesses'),
    products: (): boolean => hasPermission('products'),
    resources: (): boolean => hasPermission('resources'),
    services: (): boolean => hasPermission('services'),
    equipment: (): boolean => hasPermission('equipment')
  }

  return {
    isAdmin,
    hasPermission,
    canEdit,
    canView,
    userPermissions: effectivePermissions
  }
}
